import React, { useState, useCallback } from "react";
import FinancialDetails from "../FinancialDetails/FinancialDetails";
import CompIQSelector from "../CompIQSelector/CompIQSelector";
import RevenueTeardown from "../RevenueTeardown/RevenueTeardown";
import NewsSelector from "../NewsSelector/NewsSelector";
import DimensionsSelector, {
  DEFAULT_SELECTED_KEYS,
  DEFAULT_SELECTED_KEYS_USER,
} from "../DimensionsAndSubDimensions/DimensionsAndSubDimensions";
import CompetitorSelection from "../CompetitorSuperAdmin/competitorsuperadmin";
import * as FeatherIcon from "react-feather";

import {
  fetchDocumentsV3 as getFiles,
  getFilesTask,
  filterValidARFiles,
  wait,
  // startRevenueTeardown,
  insertFinancialsDataStandalone as insertFinancialsData,
  generateProfile,
  downloadPdfForPreview,
  getGrowthIqListing,
} from "../../../../services/accIqService";
import APP_CONFIG from "../../../../config/appConfig";
import "./ProfileCard.css";
// import { Currency } from "lucide-react";

const ProfileCard = ({ user, onSuccess }) => {
  const isSuperAdmin = user?.growth_iq_role_name === "ROLE_MNM_SUBSCRIBER";
  const isMarketIQDemo = user?.demo === APP_CONFIG?.market_iq_demo;

  //company and industry for super admin. Company selector states
  const [companySearch, setCompanySearch] = useState("");
  const [companyOptions, setCompanyOptions] = useState([]);
  const [companyLoading, setCompanyLoading] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [companySelectorError, setCompanySelectorError] = useState("");

  // State Management
  const [financialCompanyName, setFinancialCompanyName] = useState("");
  const [ciqId, setCiqId] = useState("");
  const [compiqSame, setCompiqSame] = useState(true);
  const [compiqCompanyName, setCompiqCompanyName] = useState("");
  const [compiqRegion, setCompiqRegion] = useState("");
  const [revenueTeardownEnabled, setRevenueTeardownEnabled] = useState(false);
  const [revenueTeardownFile, setRevenueTeardownFile] = useState(null);
  const [newsCompanies, setNewsCompanies] = useState([]);
  // --- Dimensions & Subdimensions selection state (flat array of child keys) ---
  const [selectedDimensions, setSelectedDimensions] = useState(
    isMarketIQDemo ? DEFAULT_SELECTED_KEYS_USER : DEFAULT_SELECTED_KEYS
  );
  const [arFilesFound, setArFilesFound] = useState(true);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploadError, setUploadError] = useState(null);

  const [expectedCurrency, setExpectedCurrency] = useState("USD");

  // PDF preview states
  const [existingPdfData, setExistingPdfData] = useState(null);
  const [showUploadOption, setShowUploadOption] = useState(false);

  // Step-based flow states
  // 'initial' - show Next button (form fields)
  // 'checkingFiles' - checking AR files (loading)
  // 'pdfPreview' - show PDF preview with proceed/upload options
  // 'ready' - show second Next button (after PDF/upload)
  // 'dimensionsStep' - show Dimensions & Subdimensions selector + Submit
  // 'processing' - executing API calls with status indicators
  // 'complete' - all done, show minimized/colored view
  const [currentStep, setCurrentStep] = useState("initial");

  // Process statuses: 'pending' | 'processing' | 'complete' | 'error'
  const [processStatuses, setProcessStatuses] = useState({
    // revenueTeardown: "pending",
    financials: "pending",
    generateProfile: "pending",
  });

  // Loading states
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingStep, setProcessingStep] = useState("");
  const [processingSubtext, setProcessingSubtext] = useState("");

  // Validation errors
  const [errors, setErrors] = useState({
    financialDetails: "",
    compiq: "",
  });

  // Success state
  const [isSuccess, setIsSuccess] = useState(false);
  const [errorBanner, setErrorBanner] = useState("");

  // Track if component is minimized after completion
  const [isMinimized, setIsMinimized] = useState(false);

  // Handler for Financial Details change
  const handleFinancialDetailsChange = useCallback((companyName, companyId) => {
    setFinancialCompanyName(companyName);
    setCiqId(companyId);
    setErrors((prev) => ({ ...prev, financialDetails: "" }));
    setArFilesFound(true);
    setIsSuccess(false);
    setErrorBanner("");
    setCurrentStep("initial");
    setIsMinimized(false);
    setExistingPdfData(null);
    setShowUploadOption(false);
    // Auto-disable Revenue Teardown when CIQ ID is missing
    if (!companyId) {
      setRevenueTeardownEnabled(false);
    }
  }, []);

  // Handler for CompIQ same toggle
  const handleCompiqSameChange = useCallback((isSame) => {
    setCompiqSame(isSame);
    setErrors((prev) => ({ ...prev, compiq: "" }));
  }, []);

  // Handler for CompIQ company name change
  const handleCompiqCompanyNameChange = useCallback((name) => {
    setCompiqCompanyName(name);
    setErrors((prev) => ({ ...prev, compiq: "" }));
  }, []);

  // Handler for CompIQ region change
  const handleCompiqRegionChange = useCallback((region) => {
    setCompiqRegion(region);
  }, []);

  // Handler for file upload
  const handleFileChange = useCallback((file, error) => {
    setUploadedFile(file);
    setUploadError(error);
  }, []);

  // Validation
  const validate = () => {
    const newErrors = {
      financialDetails: "",
      compiq: "",
    };
    let isValid = true;

    // Company Selector validation
    if (isSuperAdmin) {
      if (!selectedCompany) {
        setCompanySelectorError("Please select a company");
        isValid = false;
      } else {
        setCompanySelectorError("");
      }
    }

    // Financial Details validation
    if (!financialCompanyName.trim()) {
      newErrors.financialDetails = "Financial Details company name is required";
      isValid = false;
    }

    // CompIQ validation
    if (compiqSame === null) {
      newErrors.compiq = "Please select Yes or No for CompIQ";
      isValid = false;
    } else if (!compiqCompanyName.trim()) {
      newErrors.compiq = "CompIQ company name is required";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  // Check AR files with fallback logic
  const checkARFiles = async () => {
    setProcessingStep("Checking annual report files...");
    setProcessingSubtext("");

    try {
      // Step 1: Try to get files
      let files = await getFiles(ciqId);

      // Step 2: If no records, call fallback API
      if (!files || files.length === 0) {
        setProcessingStep("Initiating file retrieval...");
        setProcessingSubtext("This may take up to 3 minutes");

        await getFilesTask(ciqId);

        // Wait 180 seconds
        setProcessingStep("Waiting for file processing...");
        for (let i = 180; i > 0; i--) {
          setProcessingSubtext(`${i} seconds remaining`);
          await wait(1000);
        }

        // Retry get_files
        setProcessingStep("Retrying file retrieval...");
        setProcessingSubtext("");
        files = await getFiles(ciqId);
      }

      // Step 3: Filter valid files
      const validFiles = filterValidARFiles(files || []);

      if (validFiles.length === 0) {
        // No valid files found
        setArFilesFound(false);
        return { found: false, files: [] };
      }

      setArFilesFound(true);
      return { found: true, files: validFiles };
    } catch (error) {
      console.error("Error checking AR files:", error);
      setArFilesFound(false);
      return { found: false, files: [] };
    }
  };

  // Handle "Next" button click
  const handleNext = async () => {
    setErrorBanner("");
    setIsSuccess(false);

    // Step 1: Validate
    if (!validate()) {
      return;
    }

    // If Revenue Teardown is NOT enabled, skip directly to dimensions step
    if (!revenueTeardownEnabled) {
      setCurrentStep("dimensionsStep");
      return;
    }

    // If Revenue Teardown IS enabled, check AR files first
    setCurrentStep("checkingFiles");
    setIsProcessing(true);

    try {
      const result = await checkARFiles();

      if (!result.found) {
        // No valid files found - show upload UI directly
        setExistingPdfData(null);
        setShowUploadOption(true);
        setCurrentStep("ready");
      } else {
        // PDF found - extract file info for preview
        const latestFile = result.files[0]; // Use the first/latest file

        // Build initial PDF data
        const pdfData = {
          filename:
            latestFile.fileName ||
            latestFile.file_name ||
            latestFile.formDescription ||
            "Annual Report",
          filingDate: latestFile.filingDate || latestFile.filing_date,
          fileType: latestFile.fileType || latestFile.file_type,
          filingVersionId: latestFile.filingVersionId,
          url: null,
          pageCount: null,
          isLoading: true,
        };

        setExistingPdfData(pdfData);
        setShowUploadOption(false);
        setCurrentStep("pdfPreview");

        // Try to download PDF for preview in background
        try {
          const filingVersionId = latestFile.filingVersionId;
          if (filingVersionId) {
            setProcessingStep("Loading PDF preview...");
            const { url } = await downloadPdfForPreview(filingVersionId);

            setExistingPdfData((prev) => ({
              ...prev,
              url: url,
              isLoading: false,
            }));
          } else {
            console.warn("No filingVersionId found for PDF preview");
            setExistingPdfData((prev) => ({
              ...prev,
              isLoading: false,
            }));
          }
        } catch (pdfError) {
          console.error("Error loading PDF preview:", pdfError);
          setExistingPdfData((prev) => ({
            ...prev,
            isLoading: false,
          }));
        }
      }
    } catch (error) {
      console.error("Error in Next workflow:", error);
      setErrorBanner(
        "An error occurred while checking files. Please try again."
      );
      setCurrentStep("initial");
    } finally {
      setIsProcessing(false);
      setProcessingStep("");
      setProcessingSubtext("");
    }
  };

  const fetchCompanies = async (searchText) => {
    if (searchText.trim().length < 3) {
      setCompanyOptions([]);
      return;
    }

    setCompanyLoading(true);
    try {
      const res = await getGrowthIqListing(searchText);
      setCompanyOptions(res?.data || []);
    } catch (error) {
      console.error("Company search failed:", error);
      setCompanyOptions([]);
    } finally {
      setCompanyLoading(false);
    }
  };

  // Handle "Submit" button click - execute all 3 API calls
  const handleSubmit = async () => {
    setErrorBanner("");

    // Check if AR files are not found and no file uploaded (only if revenue teardown enabled)
    if (revenueTeardownEnabled && !arFilesFound && !revenueTeardownFile) {
      setErrorBanner(
        "Please upload an annual report file to continue with Revenue Teardown"
      );
      return;
    }

    setCurrentStep("processing");

    // Reset process statuses - only include enabled processes
    const initialStatuses = {
      // revenueTeardown: revenueTeardownEnabled ? "pending" : "skipped",
      financials: ciqId ? "pending" : "skipped",
      generateProfile: "pending",
    };
    setProcessStatuses(initialStatuses);

    // Execute all API calls
    const executeApiCalls = async () => {
      const promises = [];

      // Revenue Teardown (if enabled)
      // if (revenueTeardownEnabled) {
      //   promises.push(
      //     (async () => {
      //       setProcessStatuses((prev) => ({
      //         ...prev,
      //         revenueTeardown: "processing",
      //       }));
      //       try {
      //         await startRevenueTeardown(
      //           financialCompanyName,
      //           ciqId,
      //           revenueTeardownFile || null,
      //           expectedCurrency
      //         );
      //         setProcessStatuses((prev) => ({
      //           ...prev,
      //           revenueTeardown: "complete",
      //         }));
      //       } catch (error) {
      //         console.error("Revenue teardown failed:", error);
      //         setProcessStatuses((prev) => ({
      //           ...prev,
      //           revenueTeardown: "error",
      //         }));
      //       }
      //     })()
      //   );
      // }

      // Financials (only if ciqId exists)
      if (ciqId) {
        promises.push(
          (async () => {
            setProcessStatuses((prev) => ({
              ...prev,
              financials: "processing",
            }));
            try {
              await insertFinancialsData(financialCompanyName, ciqId);
              setProcessStatuses((prev) => ({
                ...prev,
                financials: "complete",
              }));
            } catch (error) {
              console.error("Financials insertion failed:", error);
              setProcessStatuses((prev) => ({ ...prev, financials: "error" }));
            }
          })()
        );
      }

      // Generate Profile
      promises.push(
        (async () => {
          setProcessStatuses((prev) => ({
            ...prev,
            generateProfile: "processing",
          }));
          try {
            // Client is the selected company for super admins, or the user's company otherwise
            const clientName = isSuperAdmin
              ? selectedCompany?.company_name_for_growth_iq ||
              selectedCompany?.company_name ||
              ""
              : user?.company_name_for_growth_iq || user?.company_name || "";

            // Competitor is the company selected in Financial Details
            const competitorName = financialCompanyName;

            //User id of the user logged in
            const user_id = user?.user_id;

            //User role of the user logged in
            const user_role = user?.growth_iq_role_name;

            //User company type default "growth_iq"

            const user_type = user?.demo === APP_CONFIG?.market_iq_demo ? "MarketIQ" : "CompIQ";

            // Extract company codes from selected news companies
            // newsCompanies is array of {label, value, type} where value is the company code
            const ksCompanies = newsCompanies.map((company) => company.value);

            // BU should be blank if it's exactly the same as the financial company name
            const buValue =
              compiqCompanyName.trim().toLowerCase() ===
                financialCompanyName.trim().toLowerCase()
                ? ""
                : compiqCompanyName;

            // Region should be blank if it's "global" (case-insensitive)
            const regionValue =
              compiqRegion.trim().toLowerCase() === "global"
                ? ""
                : compiqRegion;

            // Industry is compulsory – always pass something (even blank)
            const industryValue = isSuperAdmin
              ? typeof selectedCompany?.industry === "string"
                ? selectedCompany.industry.trim()
                : ""
              : typeof user?.industry === "string"
                ? user.industry.trim()
                : "";

            // children are now plain label arrays, so selectedDimensions already holds display labels

            //Revenue teardown to be called via generateProfile api directly
            const revenue_teardown = revenueTeardownEnabled ? true : false;
            const currencyToSend = revenueTeardownEnabled ? expectedCurrency : null;
            const pdfToSend = revenueTeardownEnabled ? (revenueTeardownFile || null) : null;

            await generateProfile(
              clientName, // client (user's company)
              competitorName, // competitor_name (from Financial Details)
              user_id,
              user_role,
              user_type,
              revenue_teardown,
              ksCompanies, // ks_companies (news company codes)
              ciqId, // company_id
              regionValue, // region (blank if "global")
              buValue, // bu (blank if same as financialCompanyName)
              industryValue,
              selectedDimensions, // flat array of display labels
              pdfToSend,
              currencyToSend
            );
            setProcessStatuses((prev) => ({
              ...prev,
              generateProfile: "complete",
            }));
          } catch (error) {
            console.error("Generate profile failed:", error);
            setProcessStatuses((prev) => ({
              ...prev,
              generateProfile: "error",
            }));
          }
        })()
      );

      await Promise.all(promises);
    };

    try {
      await executeApiCalls();

      // Small delay to show completion
      await wait(500);

      setCurrentStep("complete");
      setIsSuccess(true);

      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      console.error("Error in submit workflow:", error);
      setErrorBanner("An error occurred during processing. Please try again.");
    }
  };

  // Reset form
  const handleReset = () => {
    setFinancialCompanyName("");
    setCiqId("");
    setCompiqSame(null);
    setCompiqCompanyName("");
    setCompiqRegion("");
    setCompiqSame(true);
    setRevenueTeardownEnabled(false);
    setRevenueTeardownFile(null);
    setNewsCompanies([]);
    setSelectedDimensions([]);
    setArFilesFound(true);
    setUploadedFile(null);
    setUploadError(null);
    setErrors({ financialDetails: "", compiq: "" });
    setIsSuccess(false);
    setErrorBanner("");
    setCurrentStep("initial");
    setProcessStatuses({
      // revenueTeardown: "pending",
      financials: "pending",
      generateProfile: "pending",
    });
    setIsMinimized(false);
    setExistingPdfData(null);
    setShowUploadOption(false);
    setCompanySearch("");
    setCompanyOptions([]);
    setCompanyLoading(false);
    setSelectedCompany(null);
    setCompanySelectorError("");
  };

  // Handle proceed with existing PDF
  const handleProceedWithExisting = () => {
    // Keep existingPdfData, proceed to ready step
    setShowUploadOption(false);
    setCurrentStep("ready");
  };

  // Handle choose to upload new PDF
  const handleChooseUpload = () => {
    // Clear existing PDF data and show upload UI
    setExistingPdfData(null);
    setShowUploadOption(true);
    setCurrentStep("ready");
  };

  // Toggle minimized state
  const toggleMinimize = () => {
    setIsMinimized(!isMinimized);
  };

  // Render process status item
  const renderProcessStatus = (key, label) => {
    const status = processStatuses[key];

    if (status === "skipped") return null;

    return (
      <div className={`process-status-item ${status}`} key={key}>
        <div className="process-status-indicator">
          {status === "pending" && <span className="status-pending">○</span>}
          {status === "processing" && <span className="status-spinner" />}
          {status === "complete" && <span className="status-check">✓</span>}
          {status === "error" && <span className="status-error">✕</span>}
        </div>
        <span className="process-status-label">{label}</span>
      </div>
    );
  };

  // Determine which button to show
  const renderActionButton = () => {
    if (currentStep === "initial") {
      return (
        <button
          type="button"
          className="profile-card-button profile-card-button-primary"
          onClick={handleNext}
          disabled={isProcessing}
        >
          Next{" "}
          <FeatherIcon.ChevronRight className="comp_icon mr-1" width={"16px"} />
        </button>
      );
    }

    if (currentStep === "checkingFiles") {
      return (
        <button
          type="button"
          className="profile-card-button profile-card-button-primary"
          disabled
        >
          Checking Files...
        </button>
      );
    }

    if (currentStep === "pdfPreview") {
      // Buttons are rendered in RevenueTeardown component for preview mode
      return null;
    }

    if (currentStep === "ready") {
      return (
        <button
          type="button"
          className="profile-card-button profile-card-button-primary"
          onClick={() => setCurrentStep("dimensionsStep")}
        >
          Next{" "}
          <FeatherIcon.ChevronRight className="comp_icon mr-1" width={"16px"} />
        </button>
      );
    }

    if (currentStep === "dimensionsStep") {
      return (
        <button
          type="button"
          className="profile-card-button profile-card-button-primary"
          onClick={handleSubmit}
        >
          <FeatherIcon.Send className="comp_icon mr-1" width={"16px"} />
          Submit
        </button>
      );
    }

    if (currentStep === "processing") {
      return (
        <button
          type="button"
          className="profile-card-button profile-card-button-primary"
          disabled
        >
          Processing...
        </button>
      );
    }

    if (currentStep === "complete") {
      return (
        <button
          type="button"
          className="profile-card-button profile-card-button-success"
          onClick={toggleMinimize}
        >
          {isMinimized ? "Expand" : "Minimize"}
        </button>
      );
    }

    return null;
  };

  return (
    <div
      className={`profile-card ${currentStep === "complete" ? "complete" : ""
        } ${isMinimized ? "minimized" : ""}`}
    >
      {/* Loading Overlay - only for file checking */}
      {currentStep === "checkingFiles" && (
        <div className="profile-card-loading-overlay">
          <div className="profile-card-loading-spinner" />
          <div className="profile-card-loading-text">{processingStep}</div>
          {processingSubtext && (
            <div className="profile-card-loading-subtext">
              {processingSubtext}
            </div>
          )}
        </div>
      )}

      {/* Header */}
      <div className="profile-card-header">
        <h1 className="profile-card-title">Generate competitor profile</h1>
        {!isMinimized && (
          <p className="profile-card-subtitle">
            Configure company details for data processing
          </p>
        )}
      </div>

      {/* Minimized view - only show header and expand button */}
      {isMinimized && (
        <div className="profile-card-minimized-content">
          <div className="profile-card-success-minimal">
            <span className="profile-card-success-icon">✅</span>
            <span>Workflow completed for {financialCompanyName}</span>
          </div>
          <div className="profile-card-actions">
            <button
              type="button"
              className="profile-card-button profile-card-button-secondary"
              onClick={handleReset}
            >
              New Profile
            </button>
            <button
              type="button"
              className="profile-card-button profile-card-button-success"
              onClick={toggleMinimize}
            >
              Expand
            </button>
          </div>
        </div>
      )}

      {/* Full content - hidden when minimized */}
      {!isMinimized && (
        <>
          {/* Success Message for complete state */}
          {currentStep === "complete" && (
            <div className="profile-card-success">
              <span className="profile-card-success-icon">✅</span>
              <span className="profile-card-success-text">
                Workflow completed successfully!
              </span>
            </div>
          )}

          {/* Error Banner */}
          {errorBanner && (
            <div className="profile-card-error-banner">{errorBanner}</div>
          )}

          {/* Processing Status Panel */}
          {(currentStep === "processing" || currentStep === "complete") && (
            <div className="process-status-panel">
              <div className="process-status-title">Processing Status</div>
              <div className="process-status-list">
                {/* {revenueTeardownEnabled &&
                  renderProcessStatus("revenueTeardown", "Revenue Teardown")} */}
                {renderProcessStatus("financials", "Financial Data")}
                {renderProcessStatus("generateProfile", "Generate Profile")}
              </div>
            </div>
          )}

          {/* Main form sections - hidden during dimensions step */}
          {currentStep !== "dimensionsStep" && (
            <>
              {/* Competitor Selection */}
              {isSuperAdmin && (
                <>
                  <div className="profile-card-section">
                    <CompetitorSelection
                      search={companySearch}
                      options={companyOptions}
                      loading={companyLoading}
                      error={companySelectorError}
                      disabled={
                        currentStep === "processing" ||
                        currentStep === "complete"
                      }
                      onSearchChange={(value) => {
                        setCompanySearch(value);
                        if (selectedCompany) {
                          const selName =
                            selectedCompany.company_name_for_growth_iq || "";
                          const selIndustry =
                            typeof selectedCompany.industry === "string" &&
                              selectedCompany.industry.trim().length > 0
                              ? selectedCompany.industry.trim()
                              : null;
                          const selDisplay = selIndustry
                            ? `${selName} (${selIndustry})`
                            : selName;

                          if (value === selDisplay) {
                            return;
                          }
                          setSelectedCompany(null);
                        }
                        if (!value || value.trim().length < 3) {
                          setSelectedCompany(null);
                          setCompanyOptions([]);
                          return;
                        }
                        fetchCompanies(value);
                      }}
                      onSelect={(company) => {
                        setSelectedCompany(company);
                        setCompanySelectorError("");
                      }}
                    />
                  </div>
                  <div className="profile-card-divider" />
                </>
              )}

              {/* Financial Details */}
              <div className="profile-card-section">
                <FinancialDetails
                  value={financialCompanyName}
                  ciqId={ciqId}
                  onChange={handleFinancialDetailsChange}
                  error={errors.financialDetails}
                  disabled={
                    currentStep === "processing" || currentStep === "complete"
                  }
                />
              </div>

              <div className="profile-card-divider" />

              {/* CompIQ */}
              <div className="profile-card-section">
                <CompIQSelector
                  isSame={compiqSame}
                  companyName={compiqCompanyName}
                  region={compiqRegion}
                  financialCompanyName={financialCompanyName}
                  onSameChange={handleCompiqSameChange}
                  onCompanyNameChange={handleCompiqCompanyNameChange}
                  onRegionChange={handleCompiqRegionChange}
                  error={errors.compiq}
                  disabled={
                    currentStep === "processing" || currentStep === "complete"
                  }
                />
              </div>

              <div className="profile-card-divider" />

              {/* News Selector */}
              <div className="profile-card-section">
                <NewsSelector
                  selectedCompanies={newsCompanies}
                  onCompaniesChange={setNewsCompanies}
                  disabled={
                    currentStep === "processing" || currentStep === "complete"
                  }
                  vendorCompanyId={ciqId}
                />
              </div>

              <div className="profile-card-divider" />

              {/* Revenue Teardown */}
              <div className="profile-card-section">
                {/* <RevenueTeardown
                                enabled={revenueTeardownEnabled}
                                onEnabledChange={setRevenueTeardownEnabled}
                                disabled={currentStep === 'processing' || currentStep === 'complete'}
                                arFilesFound={arFilesFound}
                                uploadedFile={revenueTeardownFile}
                                onFileChange={(file) => setRevenueTeardownFile(file)}
                                existingPdfData={existingPdfData}
                                showPreview={currentStep === 'pdfPreview'}
                                showUploadOption={showUploadOption}
                                onProceedWithExisting={handleProceedWithExisting}
                                onChooseUpload={handleChooseUpload}
                            /> */}
                {
                  <RevenueTeardown
                    enabled={revenueTeardownEnabled}
                    onEnabledChange={setRevenueTeardownEnabled}
                    disabled={
                      currentStep === "processing" || currentStep === "complete"
                    }
                    arFilesFound={arFilesFound}
                    uploadedFile={revenueTeardownFile}
                    onFileChange={(file) => setRevenueTeardownFile(file)}
                    existingPdfData={existingPdfData}
                    showPreview={currentStep === "pdfPreview"}
                    showUploadOption={showUploadOption}
                    onProceedWithExisting={handleProceedWithExisting}
                    onChooseUpload={handleChooseUpload}
                    expectedCurrency={expectedCurrency}
                    onCurrencyChange={setExpectedCurrency}
                    noCiqId={!!financialCompanyName.trim() && !ciqId}
                  />
                }
              </div>
            </>
          )}

          {/* --- Dimensions & Subdimensions Selector (shown on second Next step) --- */}
          {currentStep === "dimensionsStep" && (
            <div className="profile-card-section">
              <DimensionsSelector
                selectedKeys={selectedDimensions}
                onSelectionChange={setSelectedDimensions}
                disabled={false}
                isMarketIQDemo={isMarketIQDemo}
              />
            </div>
          )}

          {/* Actions */}
          <div className="profile-card-actions">
            {currentStep === "dimensionsStep" && (
              <button
                type="button"
                className="profile-card-button profile-card-button-secondary"
                onClick={() =>
                  setCurrentStep(revenueTeardownEnabled ? "ready" : "initial")
                }
              >
                <FeatherIcon.ChevronLeft
                  className="comp_icon mr-1"
                  width={"16px"}
                />
                Back
              </button>
            )}
            <button
              type="button"
              className="profile-card-button profile-card-button-secondary"
              onClick={handleReset}
              disabled={
                currentStep === "processing" || currentStep === "checkingFiles"
              }
            >
              <FeatherIcon.RotateCcw
                className="comp_icon mr-1"
                width={"16px"}
              />
              Reset
            </button>
            {renderActionButton()}
          </div>
        </>
      )}
    </div>
  );
};

export default ProfileCard;
